import {NumberInput, Textarea} from '@mantine/core'
import {mapEntryEnd} from '../mapper/mapEntryEnd.ts'
import {mapEntryHours} from '../mapper/mapEntryHours.ts'
import dayjs from 'dayjs'
import {ProjectSelect} from '../components/ProjectSelect.tsx'
import type {DateRange} from '../../../components/dateRangeSelector/DateRangeSelector.tsx'
import type {UseFormReturnType} from '@mantine/form'
import type {PartialTimeEntryFormValues} from './PartialTimeEntryFormValues.ts'
import type {TimeEntryFormValues} from './TimeEntryFormValues.ts'
import {formatIsoTime} from '../formatter/formatIsoTime.ts'
import {CustomTimePicker} from '../../../components/customTimePicker/CustomTimePicker.tsx'

type Props = {
  day: DateRange
  form: UseFormReturnType<PartialTimeEntryFormValues, TimeEntryFormValues | undefined>
  disabled: boolean
}

export const TimeEntryFormFields = ({day, form, disabled: containerDisabled}: Props) => {
  const disabled = containerDisabled

  return (
    <>
      <div style={{display: 'flex', gap: 5}}>
        <div style={{flex: 1}}>
          <CustomTimePicker
            label={'Start'}
            key={form.key('start')}
            {...form.getInputProps('start')}
            value={form.values.start ? formatIsoTime(dayjs(form.values.start)) : undefined}
            onChange={(start) => {
              if (!start) return

              const [hour, minute] = start.split(':').map((part) => Number.parseInt(part))
              form.setFieldValue(
                'start',
                day.start.set('hour', hour).set('minute', minute).startOf('minute').toISOString(),
              )
            }}
            required
            disabled={disabled}
          />
        </div>
        <div style={{flex: 1}}>
          <CustomTimePicker
            label={'End'}
            key={form.key('end')}
            {...form.getInputProps('end')}
            value={form.values.end ? formatIsoTime(dayjs(form.values.end)) : undefined}
            onChange={(end) => {
              if (!end) return

              const [hour, minute] = end.split(':').map((part) => Number.parseInt(part))
              form.setFieldValue(
                'end',
                day.start.set('hour', hour).set('minute', minute).startOf('minute').toISOString(),
              )
            }}
            required
            disabled={disabled}
          />
        </div>
      </div>
      <div style={{display: 'flex', gap: 5}}>
        <NumberInput
          label={'Total'}
          value={mapEntryHours(dayjs(form.values.start), dayjs(form.values.end))}
          onValueChange={({floatValue: hours = 0}) => {
            if (!form.values.start) return
            const end = mapEntryEnd(dayjs(form.values.start), hours)
            if (!end) return
            form.setFieldValue('end', end.toISOString())
          }}
          rightSection={'h'}
          rightSectionProps={{style: {justifyContent: 'right', paddingRight: 8}}}
          rightSectionPointerEvents={'none'}
          decimalScale={2}
          fixedDecimalScale
          disabled={disabled || !form.values.start}
        />

        <NumberInput
          label={'Break'}
          key={form.key('breakHours')}
          {...form.getInputProps('breakHours')}
          value={form.values.breakHours}
          onValueChange={({floatValue}) => form.setFieldValue('breakHours', floatValue || 0)}
          rightSection={'h'}
          rightSectionProps={{style: {justifyContent: 'right', paddingRight: 8}}}
          rightSectionPointerEvents={'none'}
          min={0}
          max={mapEntryHours(dayjs(form.values.start), dayjs(form.values.end))}
          decimalScale={2}
          fixedDecimalScale
          disabled={disabled}
        />
      </div>
      <Textarea
        label={'Description'}
        key={form.key('description')}
        {...form.getInputProps('description')}
        rows={3}
        disabled={disabled}
      />
      <ProjectSelect
        key={form.key('projectId')}
        {...form.getInputProps('projectId')}
        value={form.values.projectId}
        onChange={(projectId) => projectId && form.setFieldValue('projectId', projectId)}
        required
        disabled={disabled}
      />
    </>
  )
}
